import type { FloatingPosition } from '@mantine/core';
import { Popover, Tooltip } from '@mantine/core';
import type { ReactNode } from 'react';
import { memo, useId, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import type { ITheme } from '../../utils/theme';
import Icon, { IconName } from '../icon/icon';
import type { ActionsProps } from './popover-content';
import PopoverContent from './popover-content';

export interface ActionsPopoverProps<T> extends ActionsProps<T> {
  trigger?: (onClick: () => void) => ReactNode;
  closeOnActionClick?: boolean;
  position?: FloatingPosition;
  tooltipText?: string;
}

const ActionsPopover = <T,>(props: ActionsPopoverProps<T>) => {
  const theme = useTheme();
  const popoverId = useId();

  const {
    popoverAriaLabel,
    isDisabled,
    trigger,
    closeOnActionClick,
    position = 'bottom-end',
    tooltipText,
  } = props;

  const [opened, setOpened] = useState(false);
  const onClickTrigger = () => setOpened((opened) => !opened);
  const onClickAction = closeOnActionClick ? () => setOpened(false) : undefined;

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      trapFocus
      position={position}
      id={popoverId}
    >
      <Popover.Target>
        {trigger ? (
          trigger(onClickTrigger)
        ) : (
          <Tooltip label={tooltipText} disabled={!tooltipText} position="left">
            <StyledPopoverTrigger
              opened={opened}
              disabled={isDisabled}
              data-testid="actions-popover"
              onClick={onClickTrigger}
              aria-label={popoverAriaLabel}
            >
              <StyledIcon
                name={IconName.MenuDots}
                color={
                  isDisabled
                    ? theme.palette.neutral[70]
                    : theme.palette.neutral[90]
                }
              />
            </StyledPopoverTrigger>
          </Tooltip>
        )}
      </Popover.Target>
      <PopoverContent {...props} onClickAction={onClickAction} />
    </Popover>
  );
};

export default memo(ActionsPopover) as typeof ActionsPopover;

const StyledPopoverTrigger = styled.button`
  ${({
    theme,
    opened,
    disabled,
  }: {
    theme: ITheme;
    opened?: boolean;
    disabled?: boolean;
  }) => css`
    background-color: ${opened ? theme.palette.neutral[50] : 'transparent'};
    border: none;
    padding: 0;
    width: ${theme.spacing(3.5)};
    min-width: ${theme.spacing(3.5)};
    height: ${theme.spacing(3.5)};
    border-radius: 100%;
    cursor: ${disabled ? 'not-allowed' : 'pointer'};

    &:hover {
      background-color: ${theme.palette.neutral[50]};
    }
  `}
`;

const StyledIcon = styled(Icon)`
  ${({ theme }) => css`
    display: flex;
    justify-content: center;

    svg {
      margin-top: ${theme.spacing(-2.5)};
    }
  `}
`;
